//Built-in libraries

import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
//Third party libraries
import { get, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router';
import { signApplicationPublicAsset } from '@/api/Rover/ApplicationForm';
import type { ISignApplicationPublicAssetPayload } from '@/api/Rover/ApplicationForm/types';
import Button from '@/components/UI/Button';
//Custom components
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
//Services
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
import { useFileUpload } from '@/hooks/useFileUpload';

//STP Libraries

//Assets and Miscellaneous
import type { SignedApplicationAssetResponse } from '@/types/Shared';
import { compareTwoArrays, isObjectEmpty } from '@/utils';

//Component Begin

export default function Attachments() {
  const params = useParams<string>();
  const [uploadError, setUploadError] = useState<string>('');

  const { setCurrentStep, updateData, data: formContextData } = useApplicationFormContext();

  const {
    trigger,
    getValues,
    setFocus,
    formState: { errors, isValid },
  } = useFormContext();

  useEffect(() => {
    if (errors?.attachments && !isObjectEmpty(errors)) {
      const firstErrorKey = Object.keys(errors?.attachments)[0];
      const firstErrorChildKey = Object.keys(get(errors, `attachments.${firstErrorKey}`))[0];
      setFocus(`attachments.${firstErrorKey}.${firstErrorChildKey}`);
    }
  }, [errors, setFocus]);

  const { upload } = useFileUpload({
    signAsset: signApplicationPublicAsset,
  });

  const uploadFile = useMutation(
    (
      payloads: {
        signAssetPayload: ISignApplicationPublicAssetPayload[];
        files: File[];
        field: { key: string; description: string };
      }[]
    ) => Promise.allSettled(payloads.map((p: any) => upload(p))),
    {
      onSuccess: (
        res: any[],
        payloads: {
          signAssetPayload: ISignApplicationPublicAssetPayload[];
          files: File[];
          field: { key: string; description: string };
        }[]
      ) => {
        const newData: any = {};

        res.forEach((r, resIndex) => {
          const { files, sign } = r.value;
          let signRes: SignedApplicationAssetResponse[] = [];

          if (Array.isArray(sign)) {
            signRes = [...sign];
          } else {
            signRes = [{ ...sign }];
          }

          newData[payloads[resIndex].field.key] = {
            ...formContextData.attachments[payloads[resIndex].field.key],
            selectedFiles: [
              ...signRes.map((sig, index) => ({
                name: files[index].name,
                path: sig.path,
                key: sig.key,
                type: files[index].type,
                field: payloads[resIndex].field.key,
                description: payloads[resIndex].field.description,
              })),
              ...formContextData.attachments[payloads[resIndex].field.key].selectedFiles,
            ],
          };

          updateData({
            attachments: {
              ...formContextData.attachments,
              ...newData,
            },
          });
        });
        setCurrentStep((prev) => prev + 1);
      },

      onError: (_error: any) => {
        setUploadError('Failed to upload');
      },
    }
  );
  const buildFileUploadPayload = () => {
    const formData = getValues('attachments');
    const payloads: {
      signAssetPayload: ISignApplicationPublicAssetPayload[];
      files: File[];
      field: { key: string; description: string };
    }[] = [];

    Object.keys(formData).forEach((f: string) => {
      const { selectedFiles } = formData[f];
      if (selectedFiles && selectedFiles.length > 0) {
        const formDataSelectedFiles = selectedFiles.map((f: File) => f.name);
        const formContextDataUploadedFiles: string[] = formContextData?.attachments[f].selectedFiles
          .map((sf: File) => sf.name)
          .filter((fn: string | undefined) => fn);

        const fileUploadCompare = compareTwoArrays(formDataSelectedFiles, formContextDataUploadedFiles);

        //Uploading only new file names
        if (fileUploadCompare.array1Diff.length > 0) {
          const signAssetPayload = selectedFiles
            .filter((f: File) => fileUploadCompare.array1Diff.includes(f.name))
            .map((file: File) => ({
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
              formId: String(params.applicationFormId),
            }));
          payloads.push({
            signAssetPayload,
            files: selectedFiles,
            field: { key: f, description: f },
          });
        }
      }
    });

    return payloads;
  };

  const onSubmit = async () => {
    trigger();
    if (!isValid) {
      return;
    }
    // const formData = getValues("attachments");
    const formData = formContextData.attachments;

    const fileUploadPayload = buildFileUploadPayload();
    if (fileUploadPayload.length > 0) {
      uploadFile.mutate(fileUploadPayload);
    } else {
      updateData({
        attachments: { ...formData },
      });
      setCurrentStep((prev) => prev + 1);
    }
  };

  const fileRemoveHandler = (index: number, name: string) => {
    const updatedFormContextAttachments = [...formContextData.attachments[name].selectedFiles];
    updatedFormContextAttachments.splice(index, 1);

    updateData({
      attachments: {
        ...formContextData.attachments,
        [name]: {
          ...formContextData.attachments[name],
          selectedFiles: updatedFormContextAttachments,
        },
      },
    });
  };

  return (
    <div className='sm:rounded-r-md sm:overflow-hidden'>
      <div className='py-6 px-4 space-y-6 sm:p-6'>
        <h3 className='text-lg leading-6 font-medium text-green-500'>Attachments</h3>
        <div className='my-5'>
          <h4>Please provide the following documents.</h4>
        </div>
        <HookFileInput
          name='attachments.approvalLetter.selectedFiles'
          accept='.pdf'
          label='1st Mortgage Pre-Approval Letter'
          onFileRemove={(fileIndex: number) => fileRemoveHandler(fileIndex, 'approvalLetter')}
          multiple
        />
        <HookFileInput
          name='attachments.taxReturn.selectedFiles'
          label='Most Recent Tax Return'
          onFileRemove={(fileIndex: number) => fileRemoveHandler(fileIndex, 'taxReturn')}
          multiple
        />

        <HookFileInput
          name='attachments.payStubs.selectedFiles'
          label='Two Months of Most Recent Pay Stubs'
          onFileRemove={(fileIndex: number) => fileRemoveHandler(fileIndex, 'payStubs')}
          multiple
        />
        <HookFileInput
          name='attachments.bankStatements.selectedFiles'
          label='3 Months of Bank Statements'
          onFileRemove={(fileIndex: number) => fileRemoveHandler(fileIndex, 'bankStatements')}
          multiple
        />
        {uploadError && <p className='text-tertiaryColor-normal text-md col-span-6'>{uploadError}</p>}
        <div className='flex gap-4 justify-end mt-5'>
          <Button label='Previous' btnStyle='neutral' type='button' onClick={() => setCurrentStep((prev) => prev - 1)} />
          <Button
            label={uploadFile.isLoading ? 'Uploading Files...' : 'Next'}
            btnStyle={uploadFile?.isLoading ? 'neutral' : 'primary'}
            disabled={uploadFile.isLoading}
            type='button'
            onClick={onSubmit}
          />
        </div>
      </div>
    </div>
  );
}
